import { useEffect, FC } from 'react';
import { UiIcon, UiInput, UiText, UiTextarea } from '@frontend/znanie-uikit';

import CategoriesDropdown from 'components/common/CategoriesDropdown';
import { UniversitiesSelector } from 'components/common/UniversitiesSelector';
import { UploadFiles } from 'components/common/UploadFiles';

import { useMediaQueries } from 'hooks/utils/useMediaQueries';

import * as T from './SecondStepForm.types';

import { sanitizeNumber } from 'utils/helpers';

import css from '../../Form.module.scss';

export const SecondStepForm: FC<T.ISecondStepForm> = ({ formik, user }) => {
  const { isTabletOrDesktop } = useMediaQueries();

  const {
    touched,
    errors,
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setTouched,
  } = formik;

  useEffect(() => queueMicrotask(() => setTouched({})), []);

  return (
    <>
      <div className={css.selectInputWrapper}>
        <CategoriesDropdown
          field="activity"
          tabIndex="1"
          label="Сфера деятельности*"
          placeholder="Выберите сферу деятельности из списка"
          formik={formik}
          onChange={(opt) => setFieldValue('activity', opt)}
          onBlur={() => setFieldTouched('activity', true)}
        />
      </div>

      <UiInput
        id="work"
        name="work"
        tabIndex={2}
        fluid
        label="Место работы*"
        errorLabel={touched.work && errors.work ? errors.work : undefined}
        maxLength={250}
        placeholder="Укажите место работы"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.work}
        disabled={!!user?.workPlace}
        inputSize={isTabletOrDesktop ? 'normal' : 'small'}
      />

      <UiTextarea
        id="post"
        name="post"
        tabIndex={3}
        maxLength={500}
        fluid
        label="Должность (полностью)*"
        errorLabel={touched.post && errors.post ? errors.post : undefined}
        withCounter
        placeholder="Укажите должность"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.post}
        disabled={!!user?.position}
      />
      <UiTextarea
        id="regalia"
        name="regalia"
        label="Регалии*"
        tabIndex={4}
        maxLength={500}
        fluid
        withCounter
        placeholder="Укажите"
        value={values.regalia}
        onChange={handleChange}
        onBlur={handleBlur}
        errorLabel={
          touched.regalia && errors.regalia ? errors.regalia : undefined
        }
        disabled={!!user?.regalia}
      />

      <UniversitiesSelector
        id="whichUniversity"
        tabIndex={5}
        customLabel="Образование*"
        customPlaceholder="Образование"
        defaultOption={
          values.whichUniversity
            ? { label: values.whichUniversity, value: values.whichUniversity }
            : null
        }
        onSelect={(option) => setFieldValue('whichUniversity', option?.value)}
        onBlur={() => setFieldTouched('university', true)}
        errorLabel={
          touched.whichUniversity && errors.whichUniversity
            ? errors.whichUniversity
            : undefined
        }
        disabled={!!values.whichUniversity}
        defaultCustomUniversity={values.university}
        onChangeCustomUniversity={(value) => setFieldValue('university', value)}
        onBlurCustomUniversity={() => setFieldTouched('university', true)}
        onChangeCustomUniversityCheckbox={(checked) =>
          setFieldValue('selectedCustomUniversity', checked)
        }
        valueCustomUniversity={values.university}
        errorLabelCustomUniversity={
          touched.university && errors.university
            ? errors.university
            : undefined
        }
        viewCustomUniversity
        setFieldValue={setFieldValue}
      />

      <UiInput
        id="graduationYear"
        name="graduationYear"
        label="Год окончания*"
        placeholder="Укажите год окончания"
        fluid
        tabIndex={6}
        minLength={4}
        maxLength={4}
        onChange={(e) =>
          setFieldValue('graduationYear', sanitizeNumber(e.target.value))
        }
        onBlur={handleBlur}
        value={values.graduationYear}
        errorLabel={
          (touched.graduationYear && errors.graduationYear) || undefined
        }
        disabled={!!user?.graduationYear}
      />

      <UploadFiles
        tabIndex={7}
        defaultFiles={values.fileCharter}
        multipleLimit={5}
        label="Грамоты*"
        allowExtensions={['.pdf']}
        note={'только формат .pdf'}
        onUpload={(files) => {
          setFieldValue('fileCharter', files);
        }}
        onFilesError={(error) => {
          setFieldValue('hasFileCharterFilesError', !!error);
        }}
        errorLabel={errors.fileCharter ? (errors.fileCharter as string) : ''}
      />
      <UploadFiles
        tabIndex={8}
        label="Почетные письма*"
        defaultFiles={values.fileLettersOfHonor}
        multipleLimit={1}
        allowExtensions={['.pdf']}
        note={'только формат .pdf'}
        onUpload={(files) => {
          setFieldValue('fileLettersOfHonor', files);
        }}
        onFilesError={(error) => {
          setFieldValue('hasFileLettersOfHonorFilesError', !!error);
        }}
        errorLabel={
          errors.fileLettersOfHonor ? (errors.fileLettersOfHonor as string) : ''
        }
      />

      <UiTextarea
        id="comment"
        name="comment"
        tabIndex={9}
        maxLength={500}
        fluid
        withCounter
        label="Комментарии/Темы выступления*"
        errorLabel={
          touched.comment && errors.comment ? errors.comment : undefined
        }
        placeholder="Расскажите про себя"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.comment}
        disabled={!user}
      />

      <UiInput
        id="performanceUrl"
        name="performanceUrl"
        tabIndex={10}
        fluid
        label="Ссылка / документ с выступлением*"
        errorLabel={
          touched.performanceUrl && errors.performanceUrl
            ? errors.performanceUrl
            : undefined
        }
        placeholder="https://"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.performanceUrl}
        inputSize={isTabletOrDesktop ? 'normal' : 'small'}
      />

      <div className={css.performance}>
        <UiIcon name="mark-info" size={19} color="#426fff" />
        <UiText color="grey" size="small_x">
          Ссылка на видео выступления или видео визитка о теме выступления (до 1
          минуты)
        </UiText>
      </div>
    </>
  );
};
